
import React, { useMemo, useState } from 'react';
import { useLeads, LeadStageType, ServiceType } from '../context/LeadContext';
import { usePartners } from '../context/PartnerContext';
import LeadCard from '../components/leads/LeadCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { isToday, parseISO } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LeadReassignment } from '@/components/leads/LeadReassignment';
import { PageHeader } from '@/components/layouts/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/use-toast';
import {
  Search,
  UserRound,
  Plus,
  Filter,
  CalendarDays,
  Clock,
  CheckCircle,
  Info,
  Sparkles,
  ArrowUpRight,
  Eye,
  Loader2,
  Trash2,
} from 'lucide-react';

export default function Leads() {
  const { leads, deleteLead } = useLeads();
  const { partners } = usePartners();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<LeadStageType | 'all'>('all');
  const [serviceFilter, setServiceFilter] = useState<ServiceType | 'all'>('all');
  const [queueFilter, setQueueFilter] = useState<'all' | 'unassigned' | 'today' | 'stale'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest'>('newest');
  const [leadToDelete, setLeadToDelete] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const leadsStats = useMemo(() => {
    const total = leads.length;
    const newLeads = leads.filter(l => l.stage === 'Νέο').length;
    const pendingLeads = leads.filter(l => l.stage === 'σεΕκκρεμότητα').length;
    const successLeads = leads.filter(l => l.stage === 'έκλεισεΗΔουλειά').length;
    const infoLeads = leads.filter(l => l.stage === 'ήθελεΠληροφορίες').length;
    const unassigned = leads.filter(l => !l.partnerId).length;
    const todayLeads = leads.filter(l => isToday(parseISO(l.createdAt))).length;
    const stalePending = leads.filter(l => {
      if (l.stage !== 'σεΕκκρεμότητα') return false;
      const ageHours = (Date.now() - parseISO(l.createdAt).getTime()) / (1000 * 60 * 60);
      return ageHours >= 48;
    }).length;

    const assigned = total - unassigned;
    const assignmentCoverage = total > 0 ? Math.round((assigned / total) * 100) : 0;

    return {
      total,
      newLeads,
      pendingLeads,
      successLeads,
      infoLeads,
      unassigned,
      stalePending,
      todayLeads,
      assigned,
      assignmentCoverage,
    };
  }, [leads]);

  const filteredLeads = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    let filtered = leads.filter(lead => {
      if (!normalizedSearch) return true;

      return (
        lead.name.toLowerCase().includes(normalizedSearch) ||
        lead.phone.includes(normalizedSearch) ||
        lead.address.toLowerCase().includes(normalizedSearch) ||
        lead.postalCode.toLowerCase().includes(normalizedSearch)
      );
    });

    if (stageFilter !== 'all') {
      filtered = filtered.filter(lead => lead.stage === stageFilter);
    }

    if (serviceFilter !== 'all') {
      filtered = filtered.filter(lead => lead.service === serviceFilter);
    }

    if (queueFilter === 'unassigned') {
      filtered = filtered.filter(lead => !lead.partnerId);
    }

    if (queueFilter === 'today') {
      filtered = filtered.filter(lead => isToday(parseISO(lead.createdAt)));
    }

    if (queueFilter === 'stale') {
      filtered = filtered.filter(lead => {
        if (lead.stage !== 'σεΕκκρεμότητα') return false;
        const ageHours = (Date.now() - parseISO(lead.createdAt).getTime()) / (1000 * 60 * 60);
        return ageHours >= 48;
      });
    }

    return [...filtered].sort((a, b) => {
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      return sortBy === 'newest' ? bTime - aTime : aTime - bTime;
    });
  }, [leads, queueFilter, search, serviceFilter, sortBy, stageFilter]);

  const handleDeleteClick = (leadId: string) => {
    setLeadToDelete(leadId);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!leadToDelete) return;

    try {
      setIsDeleting(true);
      await deleteLead(leadToDelete);
      toast({
        title: 'Επιτυχής διαγραφή',
        description: 'Το lead διαγράφηκε επιτυχώς.',
      });
      setShowDeleteDialog(false);
      setLeadToDelete(null);
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast({
        title: 'Αποτυχία διαγραφής',
        description: error instanceof Error ? error.message : 'Υπήρξε πρόβλημα κατά τη διαγραφή του lead.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Page Header */}
      <PageHeader
        title="Ενδιαφερόμενοι"
        description="Διαχείριση επαφών και leads"
        icon={<UserRound />}
        iconGradient="purple"
        actions={
          <>
            <Link to="/admin/leads/statistics">
              <Button variant="outline" className="border-slate-200">
                <ArrowUpRight className="mr-2 h-4 w-4" /> Στατιστικά
              </Button>
            </Link>
            <Link to="/admin/leads/assignment">
              <Button variant="gradient">
                <Plus className="mr-2 h-4 w-4" /> Ανάθεση Leads
              </Button>
            </Link>
          </>
        }
      />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          value={leadsStats.newLeads}
          title="Νέα"
          icon={<Sparkles />}
          variant="gradient"
          gradientColors="from-blue-50 to-indigo-50"
          size="compact"
        />
        <StatCard
          value={leadsStats.pendingLeads}
          title="Σε Αναμονή"
          icon={<Clock />}
          variant="gradient"
          gradientColors="from-amber-50 to-orange-50"
          size="compact"
        />
        <StatCard
          value={leadsStats.successLeads}
          title="Επιτυχίες"
          icon={<CheckCircle />}
          variant="gradient"
          gradientColors="from-emerald-50 to-teal-50"
          size="compact"
        />
        <StatCard
          value={leadsStats.infoLeads}
          title="Πληροφορίες"
          icon={<Info />}
          variant="gradient"
          gradientColors="from-slate-50 to-slate-100"
          size="compact"
        />
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Leads List - Takes 3 columns */}
        <div className="xl:col-span-3 space-y-6">
          {/* Filters */}
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Αναζήτηση ονόματος, τηλεφώνου, διεύθυνσης, Τ.Κ..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 bg-slate-50 border-slate-200"
                  />
                </div>
                <Select onValueChange={(value) => setStageFilter(value === 'all' ? 'all' : value as LeadStageType)}>
                  <SelectTrigger className="w-full md:w-48 bg-slate-50 border-slate-200">
                    <Filter className="h-4 w-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Στάδιο" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Όλα τα Στάδια</SelectItem>
                    <SelectItem value="Νέο">Νέο</SelectItem>
                    <SelectItem value="σεΕκκρεμότητα">Σε Εκκρεμότητα</SelectItem>
                    <SelectItem value="έκλεισεΗΔουλειά">Έκλεισε η Δουλειά</SelectItem>
                    <SelectItem value="ήθελεΠληροφορίες">Ήθελε Πληροφορίες</SelectItem>
                  </SelectContent>
                </Select>
                <Select onValueChange={(value) => setServiceFilter(value === 'all' ? 'all' : value as ServiceType)}>
                  <SelectTrigger className="w-full md:w-48 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="Υπηρεσία" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Όλες οι Υπηρεσίες</SelectItem>
                    <SelectItem value="εξοικονομώ">Εξοικονομώ</SelectItem>
                    <SelectItem value="αντλίεςΘερμότητας">Αντλίες Θερμότητας</SelectItem>
                    <SelectItem value="φωτοβολταϊκά">Φωτοβολταϊκά</SelectItem>
                    <SelectItem value="κλιματιστικά">Κλιματιστικά</SelectItem>
                    <SelectItem value="φυσικόΑέριο">Φυσικό Αέριο</SelectItem>
                    <SelectItem value="ηλιακά">Ηλιακά</SelectItem>
                    <SelectItem value="άλλο">Άλλο</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={(value) => setSortBy(value as 'newest' | 'oldest')}>
                  <SelectTrigger className="w-full md:w-44 bg-slate-50 border-slate-200">
                    <SelectValue placeholder="Ταξινόμηση" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Νεότερα πρώτα</SelectItem>
                    <SelectItem value="oldest">Παλαιότερα πρώτα</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={queueFilter === 'all' ? 'default' : 'outline'}
                  onClick={() => setQueueFilter('all')}
                  className="h-8"
                >
                  Όλα
                  <Badge variant="secondary" className="ml-2">{leadsStats.total}</Badge>
                </Button>
                <Button
                  size="sm"
                  variant={queueFilter === 'unassigned' ? 'default' : 'outline'}
                  onClick={() => setQueueFilter('unassigned')}
                  className="h-8"
                >
                  Χωρίς ανάθεση
                  <Badge variant="secondary" className="ml-2">{leadsStats.unassigned}</Badge>
                </Button>
                <Button
                  size="sm"
                  variant={queueFilter === 'today' ? 'default' : 'outline'}
                  onClick={() => setQueueFilter('today')}
                  className="h-8"
                >
                  <CalendarDays className="h-3.5 w-3.5 mr-1" />
                  Σήμερα
                  <Badge variant="secondary" className="ml-2">{leadsStats.todayLeads}</Badge>
                </Button>
                <Button
                  size="sm"
                  variant={queueFilter === 'stale' ? 'default' : 'outline'}
                  onClick={() => setQueueFilter('stale')}
                  className="h-8"
                >
                  Παλιά εκκρεμότητα (48h+)
                  <Badge variant="secondary" className="ml-2">{leadsStats.stalePending}</Badge>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Results count */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Βρέθηκαν <span className="font-semibold text-slate-700">{filteredLeads.length}</span> επαφές
            </p>
            {(search || stageFilter !== 'all' || serviceFilter !== 'all' || queueFilter !== 'all' || sortBy !== 'newest') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch('');
                  setStageFilter('all');
                  setServiceFilter('all');
                  setQueueFilter('all');
                  setSortBy('newest');
                }}
                className="text-blue-600 hover:text-blue-700"
              >
                Καθαρισμός φίλτρων
              </Button>
            )}
          </div>

          {/* Leads list */}
          {filteredLeads.length === 0 ? (
            <Card className="border-2 border-dashed border-slate-200 bg-slate-50">
              <CardContent className="py-16 text-center">
                <UserRound className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-medium">Δεν βρέθηκαν επαφές</p>
                <p className="text-sm text-slate-400 mt-1">Δοκιμάστε διαφορετικά φίλτρα</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredLeads.map((lead, index) => (
                <div
                  key={lead.id}
                  className="animate-slide-up"
                  style={{ animationDelay: `${index * 30}ms` }}
                >
                  {lead.stage === 'σεΕκκρεμότητα' && ((Date.now() - parseISO(lead.createdAt).getTime()) / (1000 * 60 * 60)) >= 48 && (
                    <div className="mb-2 flex justify-end">
                      <Badge variant="destructive">Χρειάζεται follow-up</Badge>
                    </div>
                  )}
                  <LeadCard
                    lead={lead}
                    onSendSMS={() => { }}
                  >
                    <Link to={`/lead/${lead.id}`}>
                      <Button
                        size="sm"
                        className="h-9 gap-2 bg-slate-900 hover:bg-slate-800"
                        aria-label={`Προβολή λεπτομερειών για ${lead.name}`}
                      >
                        <Eye className="h-4 w-4" />
                        <span className="hidden sm:inline">Προβολή</span>
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(lead.id)}
                      className="h-9 w-9 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      aria-label={`Διαγραφή lead ${lead.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </LeadCard>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar - Takes 1 column */}
        <div className="xl:col-span-1 space-y-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Λειτουργική Εικόνα</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Coverage ανάθεσης</span>
                <span className="font-semibold text-slate-800">{leadsStats.assignmentCoverage}%</span>
              </div>
              <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-slate-800"
                  style={{ width: `${leadsStats.assignmentCoverage}%` }}
                />
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div className="rounded-lg border border-slate-200 p-2">
                  <p className="text-xs text-slate-500">Ανατεθειμένα</p>
                  <p className="font-semibold text-slate-800">{leadsStats.assigned}</p>
                </div>
                <div className="rounded-lg border border-slate-200 p-2">
                  <p className="text-xs text-slate-500">Χωρίς ανάθεση</p>
                  <p className="font-semibold text-amber-600">{leadsStats.unassigned}</p>
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 p-2">
                <p className="text-xs text-slate-500">Ενεργοί συνεργάτες</p>
                <p className="font-semibold text-slate-800">{partners.filter(partner => partner.active).length}</p>
              </div>
            </CardContent>
          </Card>
          <LeadReassignment />
        </div>
      </div>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Διαγραφή Lead</DialogTitle>
            <DialogDescription>
              Είσαι σίγουρος ότι θέλεις να διαγράψεις αυτό το lead; Η ενέργεια δεν αναιρείται.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Άκυρο
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Διαγραφή...
                </>
              ) : (
                'Διαγραφή'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
